import { useQuery } from '@tanstack/react-query';
import moment, { type Moment } from 'moment';

import { get } from '@wecasa/common/src/api/requester';
import type {
  CleaningPrestation,
  CleaningPrestationOption,
  UniverseReference,
  ZoneId,
} from '@wecasa/common/src/types/commons';
import { formatDateForAPI } from '@wecasa/common/src/utils/date';

import type { PricesResponse } from '../types/prices';

const PRICES_QUERY_KEY = 'prices';

type GetPricesParams = {
  appointment?: Moment;
  duration: number | null;
  prestationReference?: CleaningPrestation['reference'];
  prestationOptionsReferences?: CleaningPrestationOption['reference'][];
  promotionCode?: string;
  universeReference: UniverseReference;
  versionedAt?: string;
  zone: ZoneId | null;
};

const api = {
  get: ({
    appointment,
    duration,
    prestationOptionsReferences = [],
    prestationReference,
    promotionCode,
    universeReference,
    zone,
    versionedAt,
  }: GetPricesParams) =>
    get('/customer/prices', {
      query: {
        appointment: appointment ? formatDateForAPI(appointment) : undefined,
        catalog_item_refs: [prestationReference, ...prestationOptionsReferences],
        duration,
        promotion_code: promotionCode,
        universe_ref: universeReference,
        zone_number: zone,
        versioned_at: versionedAt,
      },
    }),
};

export const useGetPricesQuery = ({
  appointment,
  duration,
  prestationOptionsReferences,
  prestationReference,
  promotionCode,
  universeReference,
  versionedAt,
  zone,
}: GetPricesParams) => {
  const { data: prices, error: pricesError } = useQuery<PricesResponse, Error>({
    queryKey: [
      PRICES_QUERY_KEY,
      appointment,
      duration,
      prestationOptionsReferences,
      prestationReference,
      promotionCode,
      universeReference,
      versionedAt,
      zone,
    ],
    queryFn: async () => {
      const response = await api.get({
        appointment,
        duration,
        prestationOptionsReferences,
        prestationReference,
        promotionCode,
        universeReference,
        versionedAt,
        zone,
      });
      return response.data;
    },
    enabled: !!prestationReference,
    staleTime: moment.duration(1, 'hour').asMilliseconds(),
    cacheTime: moment.duration(1, 'hour').asMilliseconds(),
  });

  return { prices, pricesError };
};
